import React, { useState, useEffect } from 'react';
import { Edit2, Trash2, Eye, EyeOff, Plus, Mail, Shield, X } from 'lucide-react';
import { userAPI } from '../../../../services/dashboardAPI';

const UserManagement = ({ showNotification }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterRole, setFilterRole] = useState('all');
  const [formData, setFormData] = useState({ name: '', email: '', role: 'event_manager' });

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
  }, [filterRole]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAll();
      if (response.success) {
        const filteredUsers = filterRole === 'all' 
          ? response.data 
          : response.data.filter(u => u.role === filterRole);
        setUsers(filteredUsers);
      } else {
        showNotification('Failed to fetch users', 'error');
      }
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!formData.name || !formData.email) {
      showNotification('Please fill all fields', 'error');
      return;
    }

    try {
      setLoading(true);
      let response;
      if (editingId) {
        response = await userAPI.update(editingId, formData);
      } else {
        response = await userAPI.create(formData);
      }

      if (response.success) {
        showNotification(
          editingId ? 'User updated successfully!' : 'User added successfully!'
        );
        setShowForm(false);
        setEditingId(null);
        setFormData({ name: '', email: '', role: 'event_manager' });
        fetchUsers();
      } else {
        showNotification('Failed to save user', 'error');
      }
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        setLoading(true);
        const response = await userAPI.delete(id);
        if (response.success) {
          showNotification('User deleted successfully!');
          fetchUsers();
        } else {
          showNotification('Failed to delete user', 'error');
        }
      } catch (error) {
        showNotification(error.message, 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleStatus = async (id) => {
    const user = users.find(u => u.id === id);
    if (!user) return;
    
    try {
      setLoading(true);
      const newStatus = user.status === 'active' ? 'inactive' : 'active';
      const response = await userAPI.update(id, { ...user, status: newStatus });
      if (response.success) {
        showNotification('User status updated!');
        fetchUsers();
      } else {
        showNotification('Failed to update user status', 'error');
      }
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setFormData(user);
    setEditingId(user.id);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '', email: '', role: 'event_manager' });
  };

  return (
    <div style={{ 
      padding: '32px', 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', 
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', color: '#1a3a52', fontSize: '32px', fontWeight: '800' }}>👥 Users</h1>
            <p style={{ margin: '0', color: '#888', fontSize: '14px', fontWeight: '500' }}>Manage team members and their roles</p>
          </div>
          <button 
            onClick={() => setShowForm(!showForm)}
            style={{ 
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '14px 28px', 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white', 
              border: 'none', 
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: '700',
              fontSize: '14px',
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.35)',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-3px)';
              e.target.style.boxShadow = '0 12px 32px rgba(102, 126, 234, 0.45)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.35)';
            }}
          >
            <Plus size={22} /> New User
          </button>
        </div>

        {/* Filter Tabs */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {['all', 'super_admin', 'event_manager', 'gallery_manager'].map(role => (
            <button
              key={role}
              onClick={() => setFilterRole(role)}
              style={{
                padding: '10px 20px',
                backgroundColor: filterRole === role ? '#667eea' : '#fff',
                color: filterRole === role ? 'white' : '#667eea',
                border: `2px solid ${filterRole === role ? '#667eea' : '#e8eef5'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '700',
                fontSize: '13px',
                transition: 'all 0.3s ease',
                boxShadow: filterRole === role ? '0 4px 12px rgba(102, 126, 234, 0.25)' : 'none',
              }}
            >
              {role === 'all' ? '👨‍💼 All Users' : role === 'super_admin' ? '🔑 Admin' : role === 'event_manager' ? '📅 Events' : '🖼️ Gallery'}
            </button>
          ))}
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div style={{ 
          background: '#fff',
          padding: '30px', 
          marginBottom: '28px',
          borderRadius: '16px',
          boxShadow: '0 12px 48px rgba(0, 0, 0, 0.12)',
          border: '1px solid rgba(102, 126, 234, 0.08)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
            <h3 style={{ margin: '0', color: '#1a3a52', fontSize: '22px', fontWeight: '800' }}>
              {editingId ? '✏️ Edit User' : '➕ New User'}
            </h3>
            <button onClick={handleCloseForm} style={{ background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#888' }}>✕</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <input type="text" placeholder="Full Name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} style={{ padding: '14px 16px', border: '2px solid #e8eef5', borderRadius: '10px', fontSize: '14px', fontWeight: '500', outline: 'none', transition: 'all 0.3s' }} onFocus={(e) => e.target.style.borderColor = '#667eea'} onBlur={(e) => e.target.style.borderColor = '#e8eef5'} />
            <input type="email" placeholder="Email Address" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} style={{ padding: '14px 16px', border: '2px solid #e8eef5', borderRadius: '10px', fontSize: '14px', fontWeight: '500', outline: 'none', transition: 'all 0.3s' }} onFocus={(e) => e.target.style.borderColor = '#667eea'} onBlur={(e) => e.target.style.borderColor = '#e8eef5'} />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: '700', color: '#1a3a52', fontSize: '14px' }}>👔 Role</label>
            <select value={formData.role} onChange={(e) => setFormData({...formData, role: e.target.value})} style={{ width: '100%', padding: '14px 16px', border: '2px solid #e8eef5', borderRadius: '10px', fontSize: '14px', fontWeight: '500', outline: 'none', cursor: 'pointer', transition: 'all 0.3s' }} onFocus={(e) => e.target.style.borderColor = '#667eea'} onBlur={(e) => e.target.style.borderColor = '#e8eef5'}>
              <option value="event_manager">Event Manager</option>
              <option value="gallery_manager">Gallery Manager</option>
              <option value="logo_manager">Logo Manager</option>
              <option value="media_manager">Media Manager</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={handleAddUser} disabled={loading} style={{ flex: 1, padding: '14px 20px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: '10px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '14px', opacity: loading ? 0.65 : 1, transition: 'all 0.3s', boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)' }} onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-2px)')} onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}>{loading ? '⏳ Saving...' : editingId ? '💾 Update User' : '✨ Add User'}</button>
            <button onClick={handleCloseForm} style={{ flex: 1, padding: '14px 20px', backgroundColor: '#f0f4f8', color: '#1a3a52', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '14px', transition: 'all 0.3s' }} onMouseEnter={(e) => e.target.style.backgroundColor = '#e8eef5'} onMouseLeave={(e) => e.target.style.backgroundColor = '#f0f4f8'}>✕ Cancel</button>
          </div>
        </div>
      )}

      {/* Users Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '80px 20px' }}>
          <p style={{ fontSize: '16px', color: '#888', fontWeight: '600' }}>⏳ Loading users...</p>
        </div>
      ) : users.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 20px', background: '#fff', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}>
          <p style={{ fontSize: '18px', color: '#1a3a52', fontWeight: '700', marginBottom: '8px' }}>📭 No users yet</p>
          <p style={{ fontSize: '14px', color: '#888' }}>Click "New User" to add your first team member!</p>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)', border: '1px solid rgba(102, 126, 234, 0.08)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <th style={{ padding: '18px 20px', textAlign: 'left', fontWeight: '700', fontSize: '13px' }}>👤 Name</th>
                <th style={{ padding: '18px 20px', textAlign: 'left', fontWeight: '700', fontSize: '13px' }}>✉️ Email</th>
                <th style={{ padding: '18px 20px', textAlign: 'left', fontWeight: '700', fontSize: '13px' }}>👔 Role</th>
                <th style={{ padding: '18px 20px', textAlign: 'left', fontWeight: '700', fontSize: '13px' }}>Status</th>
                <th style={{ padding: '18px 20px', textAlign: 'center', fontWeight: '700', fontSize: '13px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} style={{ borderBottom: '1px solid #f0f4f8', transition: 'all 0.3s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}>
                  <td style={{ padding: '16px 20px', color: '#1a3a52', fontWeight: '700', fontSize: '14px' }}>{user.name}</td>
                  <td style={{ padding: '16px 20px', color: '#666', fontSize: '13px' }}>{user.email}</td>
                  <td style={{ padding: '16px 20px' }}>
                    <span style={{ display: 'inline-block', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: '700' }}>
                      {user.role.replace('_', ' ')}
                    </span>
                  </td>
                  <td style={{ padding: '16px 20px' }}>
                    <button 
                      onClick={() => toggleStatus(user.id)}
                      style={{
                        padding: '6px 16px',
                        backgroundColor: user.status === 'active' ? '#d4edda' : '#f8d7da',
                        color: user.status === 'active' ? '#155724' : '#721c24',
                        border: `1px solid ${user.status === 'active' ? '#c3e6cb' : '#f5c6cb'}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '700',
                        transition: 'all 0.3s'
                      }}
                      onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                      onMouseLeave={(e) => e.target.style.opacity = '1'}
                    >
                      {user.status === 'active' ? '✅ Active' : '⏹️ Inactive'}
                    </button>
                  </td>
                  <td style={{ padding: '16px 20px', textAlign: 'center', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                    <button onClick={() => handleEdit(user)} disabled={loading} style={{ padding: '8px 12px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', transition: 'all 0.3s' }} onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'} onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}><Edit2 size={14} /></button>
                    <button onClick={() => handleDelete(user.id)} disabled={loading} style={{ padding: '8px 12px', backgroundColor: '#fee', color: '#e74c3c', border: '1px solid #fcc', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '700', transition: 'all 0.3s' }} onMouseEnter={(e) => { e.target.style.backgroundColor = '#fcc'; e.target.style.color = '#c0392b'; }} onMouseLeave={(e) => { e.target.style.backgroundColor = '#fee'; e.target.style.color = '#e74c3c'; }}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
